import {
  createAuthenticatedApi,
  createOpenApiConfiguration,
  getApiErrorMessage,
} from "@/lib/utils/api";
import { GlobalApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";
import {
  DEFAULT_STUDIO_SIGNALS_SETTINGS,
  DEFAULT_STUDIO_SIGNALS_SETTINGS_PERSISTED,
  normalizeStudioSignalsSettings,
  parseStudioSignalsSettingsFromMap,
  serializeRoleSignalConfig,
  SIGNALS_ADMIN_CONFIG_KEY,
  SIGNALS_MEMBER_CONFIG_KEY,
  SIGNALS_SETTINGS_GROUP,
  SIGNALS_STAFF_CONFIG_KEY,
  SIGNALS_TEACHER_CONFIG_KEY,
  type StudioSignalsSettings,
  type StudioSignalsSettingsPersisted,
  type TodaySignalRole,
} from "./signals-settings-defaults";

export type StudioSignalsSettingsResult = StudioSignalsSettings & {
  persisted: StudioSignalsSettingsPersisted;
};

const ROLE_CONFIG_KEYS: Record<TodaySignalRole, string> = {
  admin: SIGNALS_ADMIN_CONFIG_KEY,
  staff: SIGNALS_STAFF_CONFIG_KEY,
  teacher: SIGNALS_TEACHER_CONFIG_KEY,
  member: SIGNALS_MEMBER_CONFIG_KEY,
};

function createGlobalApi(accessToken?: string): GlobalApi {
  if (accessToken) {
    return createAuthenticatedApi(GlobalApi, accessToken);
  }
  return new GlobalApi(createOpenApiConfiguration());
}

function isNotFoundOrBadRequest(error: unknown): boolean {
  return (
    error instanceof ResponseError &&
    (error.response.status === 404 || error.response.status === 400)
  );
}

function settingsMapFromRows(
  rows: { key: string; value: string }[],
): Map<string, string> {
  return new Map(rows.map((row) => [row.key, row.value]));
}

export async function fetchStudioSignalsSettings(
  tenantId: number,
  options?: { accessToken?: string },
): Promise<StudioSignalsSettingsResult> {
  const api = createGlobalApi(options?.accessToken);

  try {
    const rows = await api.apiGlobalSettingsByGroupGet({
      group: SIGNALS_SETTINGS_GROUP,
      xTenantID: tenantId,
    });
    const map = settingsMapFromRows(rows);
    const { settings, persisted } = parseStudioSignalsSettingsFromMap(map);

    return {
      ...settings,
      persisted,
    };
  } catch (error) {
    if (!isNotFoundOrBadRequest(error)) {
      // Fall through to defaults when group is missing.
    }
    return {
      ...DEFAULT_STUDIO_SIGNALS_SETTINGS,
      persisted: { ...DEFAULT_STUDIO_SIGNALS_SETTINGS_PERSISTED },
    };
  }
}

async function upsertSignalsSetting(
  tenantId: number,
  key: string,
  value: string,
  accessToken: string,
  persisted: boolean,
  errorLabel: string,
): Promise<void> {
  const api = createAuthenticatedApi(GlobalApi, accessToken);

  const createRequest = {
    xTenantID: tenantId,
    createSettingRequest: {
      key,
      value,
      group: SIGNALS_SETTINGS_GROUP,
    },
  };
  const updateRequest = {
    xTenantID: tenantId,
    updateSettingRequest: {
      key,
      value,
      group: SIGNALS_SETTINGS_GROUP,
    },
  };

  const throwUpsertError = async (error: unknown, action: "create" | "update") => {
    const message = await getApiErrorMessage(
      error,
      `Failed to ${action} ${errorLabel}`,
    );
    throw new Error(message);
  };

  if (persisted) {
    try {
      await api.apiGlobalSettingsPut(updateRequest);
      return;
    } catch (error) {
      if (!isNotFoundOrBadRequest(error)) {
        await throwUpsertError(error, "update");
      }
    }
  }

  try {
    await api.apiGlobalSettingsPost(createRequest);
    return;
  } catch (error) {
    if (!(error instanceof ResponseError && error.response.status === 409)) {
      await throwUpsertError(error, "create");
    }
  }

  try {
    await api.apiGlobalSettingsPut(updateRequest);
  } catch (error) {
    await throwUpsertError(error, "update");
  }
}

export async function upsertStudioSignalsSettings(
  tenantId: number,
  request: StudioSignalsSettingsResult,
  accessToken: string,
): Promise<StudioSignalsSettingsResult> {
  const normalized = normalizeStudioSignalsSettings(request);
  const roles: TodaySignalRole[] = ["admin", "staff", "teacher", "member"];

  await Promise.all(
    roles.map((role) =>
      upsertSignalsSetting(
        tenantId,
        ROLE_CONFIG_KEYS[role],
        serializeRoleSignalConfig(normalized[role]),
        accessToken,
        request.persisted[role],
        `${role} dashboard signals setting`,
      ),
    ),
  );

  return {
    ...normalized,
    persisted: {
      admin: true,
      staff: true,
      teacher: true,
      member: true,
    },
  };
}
